import React, { useEffect } from "react";
import { Modal, ModalTitle, ModalActions, ModalContent, Button } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";

interface ConfirmationModalProps {
    uploadInProgress: boolean;
    unSavedChanges: boolean;
    confirmationModalAction: (() => void) | undefined;
    onCancel: () => void;
}

/**
 * creation tool confirmation modal
 * the conditions to showing the modal may change as upload is being done
 * in the background, leading to the modal content changing or closing.
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    uploadInProgress,
    unSavedChanges,
    confirmationModalAction,
    onCancel,
}) => {
    const onCancelCallback = useEventHandler(onCancel);

    useEffect(() => {
        // Reset the confirmation action state when upload completes, causing the modal to close.
        confirmationModalAction && !uploadInProgress && !unSavedChanges && onCancelCallback();
    }, [confirmationModalAction, onCancelCallback, uploadInProgress, unSavedChanges]);

    return (
        <Modal
            variant={"task"}
            open={!!confirmationModalAction && (uploadInProgress || unSavedChanges)}
            onClose={(_, reason) => {
                if (reason !== "backdropClick") {
                    onCancelCallback();
                }
            }}
        >
            {!uploadInProgress && unSavedChanges && (
                <>
                    <ModalTitle>{translate("Unsaved Changes")}</ModalTitle>
                    <ModalContent>
                        {translate("Changes aren't saved. Are you sure you want to leave without saving them?")}
                    </ModalContent>
                    <ModalActions>
                        <Button variant={"borderless"} color={"secondary"} onClick={() => confirmationModalAction?.()}>
                            {translate("Leave")}
                        </Button>
                        <Button onClick={onCancelCallback}>{translate("Continue editing")}</Button>
                    </ModalActions>
                </>
            )}
            {uploadInProgress && (
                <>
                    <ModalTitle>{translate("Upload in progress")}</ModalTitle>
                    <ModalContent>
                        {translate("You have uploads in progress. Are you sure you want to cancel them?")}
                    </ModalContent>
                    <ModalActions>
                        <Button color="danger" onClick={() => confirmationModalAction?.()}>
                            {translate("Cancel upload")}
                        </Button>
                        <Button onClick={onCancelCallback}>{translate("Continue uploading")}</Button>
                    </ModalActions>
                </>
            )}
        </Modal>
    );
};

export default ConfirmationModal;
